/**
 * Per-project maintenance mode.
 *
 * RaceSight has no `admin.projects.status` column (Alinghi does). The header is stored as a
 * project object — `{class}.project_objects` with `object_name = 'header'`, `date = 1970-01-01`,
 * json `{"header": "..."}` — so the maintenance flag rides along in that same object:
 *
 *   { "header": "GP50 SAILING", "maintenance": { "active": true, "message": "..." } }
 *
 * An object with only `header` (every row written before this feature) reads back as READY.
 */

export type ProjectStatus = 'READY' | 'MAINTENANCE';

/** Shown when maintenance is on but the admin left the message blank. */
export const DEFAULT_MAINTENANCE_MESSAGE =
  'This project is under maintenance. Scripts may be running or project data may be temporarily unavailable.';

/** Cap on the admin-entered message, enforced on both the input and the parse. */
export const MAINTENANCE_MESSAGE_MAX_LENGTH = 300;

export function normalizeProjectStatus(value: unknown): ProjectStatus {
  const raw = value == null ? '' : String(value).trim().toUpperCase();
  return raw === 'MAINTENANCE' ? 'MAINTENANCE' : 'READY';
}

export function isProjectUnderMaintenance(status: ProjectStatus): boolean {
  return status === 'MAINTENANCE';
}

/**
 * The message to display: the admin's text if there is one, otherwise the default.
 * Truncated defensively — the textarea has a maxlength, but stored json may predate it.
 */
export function resolveMaintenanceMessage(raw: unknown): string {
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text) {
    return DEFAULT_MAINTENANCE_MESSAGE;
  }
  return text.length > MAINTENANCE_MESSAGE_MAX_LENGTH
    ? text.slice(0, MAINTENANCE_MESSAGE_MAX_LENGTH)
    : text;
}

/** The admin's raw stored message (no default substituted) — for seeding the edit field. */
export function rawMaintenanceMessage(raw: unknown): string {
  const text = typeof raw === 'string' ? raw.trim() : '';
  return text.length > MAINTENANCE_MESSAGE_MAX_LENGTH
    ? text.slice(0, MAINTENANCE_MESSAGE_MAX_LENGTH)
    : text;
}

export interface ProjectHeaderObject {
  /** '' when the object carries no usable header — callers apply their own fallback. */
  header: string;
  status: ProjectStatus;
  /** Display-ready: never empty (falls back to DEFAULT_MAINTENANCE_MESSAGE). */
  message: string;
  /** As stored, '' when unset. Use this to seed the admin textarea. */
  rawMessage: string;
}

/**
 * Parse the value returned by `GET /projects/object?object_name=header`. The API hands back the
 * jsonb column, which arrives as a JSON string from some callers and an already-parsed object from
 * others, so both are accepted. Malformed input degrades to READY rather than throwing.
 */
export function parseProjectHeaderObject(raw: unknown): ProjectHeaderObject {
  let parsed: unknown = raw;

  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }

  if (!parsed || typeof parsed !== 'object') {
    return { header: '', status: 'READY', message: DEFAULT_MAINTENANCE_MESSAGE, rawMessage: '' };
  }

  const obj = parsed as Record<string, unknown>;
  const header = typeof obj.header === 'string' ? obj.header.trim() : '';

  const maintenance =
    obj.maintenance && typeof obj.maintenance === 'object'
      ? (obj.maintenance as Record<string, unknown>)
      : {};

  const status: ProjectStatus = maintenance.active === true ? 'MAINTENANCE' : 'READY';

  return {
    header,
    status,
    message: resolveMaintenanceMessage(maintenance.message),
    rawMessage: rawMaintenanceMessage(maintenance.message),
  };
}

/**
 * Writer counterpart to `parseProjectHeaderObject`, so the reader and writer cannot drift on key
 * names. `message` is stored as typed (trimmed); the default is applied at display time only.
 */
export function buildProjectHeaderObjectJson(
  header: string,
  maintenanceActive: boolean,
  message: string
): string {
  return JSON.stringify({
    header: (header ?? '').trim(),
    maintenance: {
      active: maintenanceActive === true,
      message: rawMaintenanceMessage(message),
    },
  });
}
